import type {List} from 'immutable';
import {
  Component,
  Expressions,
  Plugins,
  matchExpression,
} from '@packages/systems/wfdl';
import {
  getName,
  getPluginName,
  qualify,
  QualifiedMap,
} from '@packages/systems/wfdl-Qualified';
import {
  EElement,
  ELiteral,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import type {DesignerStores} from '@packages/systems/designer/flux';
import type {
  XscpData,
  XscpLibraryComponent,
  XscpLibraryImport,
} from '@packages/systems/core/records/Xscp';
import {LIBRARY_SOURCE, LIBRARY_STATUS} from '@packages/systems/libraries';
import {
  getComponentByOrigin,
  isUserComponent,
} from '@packages/utilities/platform';
import {isRichText} from '@packages/utilities/elementTypeUtils';
import {getPluginsFromState} from '@packages/utilities/platform-selectors';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {Tree} from '@packages/systems/tree';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {
  prepareLibraryComponent,
  type CreateLibraryComponentResult,
} from '../../Libraries/utils/createLibraryComponent';

import {readReferences, resolveLibraryCopyTree} from './resolveLibraryCopyTree';

const hasInstalledComponent = (
  state: DesignerStores,
  reference: XscpLibraryComponent
): boolean => {
  const [pluginId, componentId] = reference.origin;
  const library = state.DesignerStore.libraries[pluginId];
  const origin = qualify(Plugins.name(pluginId), Component.name(componentId));
  const plugins = getPluginsFromState(state);
  // pluginId identifies the installed version. An available upgrade is harmless
  // as long as that version and its library profile still match the copied instance.
  return Boolean(
    library &&
    library.source === LIBRARY_SOURCE.WORKSPACE &&
    (library.status === LIBRARY_STATUS.installed ||
      library.status === LIBRARY_STATUS.upgrade_available) &&
    library.type === 'LIBRARY' &&
    library.pluginId === pluginId &&
    library.profileId === reference.profileId &&
    Plugins.getElementPreset(plugins, origin) &&
    Plugins.getElementPresetImportStrategy(plugins, origin) ===
      Plugins.IMPORT_STRATEGY.VALUE
  );
};

const getAddedIx2Records = <T extends {id: string}>(
  before: List<T>,
  after: List<T>
): List<T> => {
  if (before === after) return after.clear();
  const existingIds = new Set(before.map((item) => item.id));
  return after.filter((item) => !existingIds.has(item.id));
};

export type SharedLibraryPastePlan = {
  element: ExpressionElement;
  libraryImport: XscpLibraryImport;
  xscpData: XscpData;
};

const removeDeadSharedLibraryLinks = (element: ExpressionElement) => {
  const copiedElementIds = new Set(
    Object.keys(Expressions.allElements(EElement(element)))
  );
  let droppedLinks = 0;
  const expression = Expressions.everywhere(
    matchExpression({
      Literal: (literal, expr) => {
        const value = literal.value;
        if (
          getPluginName(literal.name) !== 'Basic' ||
          getName(literal.name) !== 'Link' ||
          !value ||
          typeof value !== 'object' ||
          Array.isArray(value)
        )
          return expr;
        if (
          value.mode === 'page' ||
          (value.mode === 'section' &&
            (typeof value.domId !== 'string' ||
              !copiedElementIds.has(value.domId)))
        ) {
          droppedLinks++;
          return ELiteral({...literal, value: {mode: 'external', href: '#'}});
        }
        return expr;
      },
      _: (expr) => expr,
    })
  )(EElement(element));
  return {
    element: Expressions.getElement(expression) ?? element,
    droppedLinks,
  };
};

export function prepareSharedLibraryPaste(
  state: DesignerStores,
  clipboard: XscpData
): SharedLibraryPastePlan | null {
  if (
    !clipboard.payload.libraryRetention ||
    clipboard.payload.expandUserComponents !== true
  )
    return null;
  // An expanded parent can still contain eligible library children, including
  // slot content, so choose retained or expanded content throughout the copy tree.
  const data = resolveLibraryCopyTree(
    clipboard.payload.libraryRetention,
    (reference) => {
      const origin = qualify(
        Plugins.name(reference.origin[0]),
        Component.name(reference.origin[1])
      );
      return (
        hasInstalledComponent(state, reference) &&
        Boolean(
          getComponentByOrigin(origin, state.DesignerStore.components)
            .componentId || selectDesignerAbility('canCreateSymbols')(state)
        )
      );
    }
  );
  const references = readReferences(data);
  if (!references) {
    return null;
  }

  const byId = new Map(
    references.map((reference) => [reference.id, reference])
  );
  const usedIds = new Set<string>();
  for (const node of data.payload.nodes as JSONNode[]) {
    if (node.type !== 'Symbol') continue;
    const sourceId = node.data?.sym?.inst;
    if (typeof sourceId !== 'string' || !byId.has(sourceId)) return null;
    usedIds.add(sourceId);
  }
  if (usedIds.size === 0) return null;

  const plugins = getPluginsFromState(state);
  let prepared: CreateLibraryComponentResult = {
    componentMap: state.DesignerStore.components,
    styleBlockState: state.StyleBlockStore,
    ix2State: state.IX2UiStore,
    ix3State: state.IX3Store,
    assetsToImport: {},
  };
  const componentIds: Record<string, string> = Object.create(null);
  for (const id of usedIds) {
    const reference = byId.get(id);
    if (!reference) return null;
    const origin = qualify(
      Plugins.name(reference.origin[0]),
      Component.name(reference.origin[1])
    );
    // Installed components acquire local definitions on first use. Consult the
    // accumulated map so this paste can also reuse definitions it just prepared.
    if (!getComponentByOrigin(origin, prepared.componentMap).componentId) {
      if (!selectDesignerAbility('canCreateSymbols')(state)) return null;
      const imported = prepareLibraryComponent({
        ...prepared,
        origin,
        // Temporary preparation state; the paste reducer commits the asset delta.
        // Let subsequent component imports reuse assets staged earlier in this plan.
        // eslint-disable-next-line webflow/restrict-asset-state-manipulation
        assets: state.AssetStore.assets.merge(prepared.assetsToImport),
        plugins,
        cssVariables: state.CssVariablesStore,
        currentPageId: state.DesignerStore.currentPageId,
        libraryProfileId:
          state.DesignerStore.libraries[getPluginName(origin)]?.profileId,
      });
      prepared = {
        ...imported,
        assetsToImport: {
          ...prepared.assetsToImport,
          ...imported.assetsToImport,
        },
      };
    }
    const local = getComponentByOrigin(origin, prepared.componentMap);
    if (
      !local.componentId ||
      !local.component ||
      Component.getSource(local.component)
    ) {
      return null;
    }
    componentIds[id] = getName(local.componentId);
  }

  const nodes = (data.payload.nodes as JSONNode[]).map((node) => {
    const sym = node.data?.sym;
    if (node.type !== 'Symbol' || !sym) return node;
    return {
      ...node,
      data: {
        ...node.data,
        sym: {...sym, inst: componentIds[sym.inst]},
      },
    };
  });
  const element = Tree.create(prepared.componentMap).getElementWithJSONNodes(
    nodes,
    plugins
  );
  if (!element) return null;
  const allowedTypes = new Set(Object.values(componentIds));
  if (
    Expressions.some(
      matchExpression({
        Element: (el) =>
          (isUserComponent(el.type) && !allowedTypes.has(getName(el.type))) ||
          (isRichText(el) && containsUserComponent(el.data)),
        _: () => false,
      })
    )(EElement(element))
  )
    return null;

  const {element: preparedElement, droppedLinks} =
    removeDeadSharedLibraryLinks(element);

  // Commit deltas into the current stores after any asset wait, preserving
  // unrelated destination changes made while the paste was pending.
  const beforeStyles = state.StyleBlockStore;
  const afterStyles = prepared.styleBlockState;
  const beforeIx2 = state.IX2UiStore;
  const afterIx2 = prepared.ix2State;
  return {
    element: preparedElement,
    xscpData: {
      ...data,
      meta: {...data.meta, droppedLinks: data.meta.droppedLinks + droppedLinks},
    },
    libraryImport: {
      element: preparedElement,
      references: references.filter((ref) => usedIds.has(ref.id)),
      componentIds,
      components:
        prepared.componentMap === state.DesignerStore.components
          ? QualifiedMap.empty()
          : prepared.componentMap.filter(
              (_, id) => !state.DesignerStore.components.has(id)
            ),
      styles: {
        styleBlocks:
          beforeStyles.styleBlocks === afterStyles.styleBlocks
            ? afterStyles.styleBlocks.clear()
            : afterStyles.styleBlocks.filter(
                (block, id) => beforeStyles.styleBlocks.get(id) !== block
              ),
        parentIndex:
          beforeStyles.parentIndex === afterStyles.parentIndex
            ? afterStyles.parentIndex.clear()
            : afterStyles.parentIndex.filter(
                (parent, id) => beforeStyles.parentIndex.get(id) !== parent
              ),
        globalOrder:
          beforeStyles.globalOrder === afterStyles.globalOrder
            ? afterStyles.globalOrder.clear()
            : afterStyles.globalOrder.subtract(beforeStyles.globalOrder),
        styleBlockIds:
          beforeStyles.styleBlockIds === afterStyles.styleBlockIds
            ? afterStyles.styleBlockIds.clear()
            : afterStyles.styleBlockIds.subtract(beforeStyles.styleBlockIds),
        cssPropertyMap:
          beforeStyles.cssPropertyMap === afterStyles.cssPropertyMap
            ? afterStyles.cssPropertyMap.clear()
            : afterStyles.cssPropertyMap.filter(
                (value, id) => beforeStyles.cssPropertyMap.get(id) !== value
              ),
      },
      ix2: {
        events: getAddedIx2Records(beforeIx2.events, afterIx2.events),
        actionLists: getAddedIx2Records(
          beforeIx2.actionLists,
          afterIx2.actionLists
        ),
        interactions: getAddedIx2Records(
          beforeIx2.interactions,
          afterIx2.interactions
        ),
      },
      ix3: {
        interactions:
          prepared.ix3State.interactions === state.IX3Store.interactions
            ? []
            : Object.values(prepared.ix3State.interactions).filter(
                (item) => !state.IX3Store.interactions[item.id]
              ),
        timelines:
          prepared.ix3State.timelines === state.IX3Store.timelines
            ? []
            : Object.values(prepared.ix3State.timelines).filter(
                (item) => !state.IX3Store.timelines[item.id]
              ),
      },
      assetsToImport: prepared.assetsToImport,
      pageId: state.DesignerStore.currentPageId,
      selection: {
        selectedNodeNativeId: state.UiNodeStore.selectedNodeNativeId,
        selectedNativeIdPath: state.UiNodeStore.selectedNativeIdPath,
        focusedSymbolNativeIdPath: state.UiNodeStore.focusedSymbolNativeIdPath,
      },
    },
  };
}

const containsUserComponent = Expressions.some(
  matchExpression({
    Element: (el) => isUserComponent(el.type),
    _: () => false,
  })
);

export function canCompleteSharedLibraryPaste(
  state: DesignerStores,
  libraryImport: XscpLibraryImport
): boolean {
  // The Designer remains interactive during asset loading. A prepared paste
  // belongs to its original page and component-editing context.
  if (
    state.DesignerStore.currentPageId !== libraryImport.pageId ||
    !selectDesignerAbility('canAddElementToCanvas')(state)
  )
    return false;
  const focusedPath = state.UiNodeStore.focusedSymbolNativeIdPath;
  const originalPath = libraryImport.selection.focusedSymbolNativeIdPath;
  if (
    focusedPath.length !== originalPath.length ||
    focusedPath.some((id, index) => id !== originalPath[index])
  )
    return false;
  // Only explicitly retained references are rechecked. A nested dependency
  // imported during the wait can still receive a second local definition.
  return libraryImport.references.every((reference) => {
    if (!hasInstalledComponent(state, reference)) return false;
    const expectedId = libraryImport.componentIds[reference.id];
    if (!expectedId) return false;
    const origin = qualify(
      Plugins.name(reference.origin[0]),
      Component.name(reference.origin[1])
    );
    const existing = getComponentByOrigin(
      origin,
      state.DesignerStore.components
    ).componentId;
    return existing
      ? getName(existing) === expectedId
      : libraryImport.components.has(
          qualify(Plugins.name('__SitePlugin'), Component.name(expectedId))
        ) && selectDesignerAbility('canCreateSymbols')(state);
  });
}
